import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { asksForALink, guardOwnWords, letterFooter, letterFrame, signature, wordsIn, LETTER_MAX_WORDS, type SetupResponse } from "@joogoos/contracts";
import { api } from "../../api.js";
import { Icon } from "../../ui/icons.js";
import { Card } from "./chrome.js";

/**
 * **The letter** — the business's own words, shown inside the letter they end up in: the usual one in Settings,
 * and a group's own on its Letters tab (38).
 *
 * This asked for an offer and an ask in two textareas and drew neither, so the only way to find out what had been
 * written was to send one and read it. That is how Overbill's offer came to be a paragraph lifted off its own
 * website: nothing on this screen ever showed it as the thing a stranger receives.
 *
 * So the whole letter is here, in order, and the two parts a workspace owns are typed in place. The rest is drawn
 * as it will be sent and cannot be edited here — the frame is fixed per language, the opening line and the subject
 * are written for each company from its own evidence (the note said the opening line alone, 2026-10-12: the model
 * writes the subject too, `outreach_opener`), the sign-off comes from the mailbox and the Business page, and the legal
 * footer belongs to nobody ([ADR 0046](../../../../../docs/adr/0046-a-cold-letter-is-plain-text-signs-off-with-the-business-and-never-repeats-itself.md)).
 *
 * The company in it is **Example Ltd** rather than a real one: a preview that names somebody the owner has never
 * written to would be read as a letter that went.
 */

const EXAMPLE = { company: "Example Ltd", toEmail: "hello@example.com" };

/**
 * What the opening line does, in the shape one takes. Never sent; it is the only line here that is an example.
 *
 * It says what was read and stops, and it belongs to no trade: the one before spoke of a delivery page and the
 * invoice the sender would look at first, which was Overbill's letter showing on every workspace's Set up.
 */
const EXAMPLE_OPENERS: Record<string, string> = {
  en: "Your About page says every table you sell is made in your own workshop in Leeds, from wood you buy locally.",
  fr: "Votre page À propos dit que chaque table que vous vendez est fabriquée dans votre atelier de Lyon, avec du bois acheté sur place.",
  es: "Su página «Quiénes somos» dice que cada mesa que venden se hace en su propio taller de Valencia, con madera comprada en la zona.",
};

/**
 * The business's usual letter, in Settings: what a group with no letter of its own says (38). Its never-write words
 * are their own card now (`NeverWords`), because they hold every letter of every group, not this one.
 */
export function Letter({ d, keep }: { d: SetupResponse; keep: (d: SetupResponse) => void }) {
  const save = useMutation({ mutationFn: (s: unknown) => api.saveCustomerSettings(s), onSuccess: keep });
  const [offer, setOffer] = useState(d.settings.offer);
  const [ask, setAsk] = useState(d.settings.ask);
  return (
    <Card title="Your usual letter" aside={<span className="text-xs text-muted">what a group says when it has no letter of its own · as {EXAMPLE.company} would read it</span>}>
      <LetterSheet
        d={d} language={d.settings.languages[0] ?? "en"} mayEdit={d.mayEdit}
        offer={offer} ask={ask} onOffer={setOffer} onAsk={setAsk}
        onOfferDone={() => offer !== d.settings.offer && save.mutate({ offer })}
        onAskDone={() => ask !== d.settings.ask && save.mutate({ ask })}
      />
    </Card>
  );
}

/**
 * One letter drawn whole, in order, with the owner's two parts typed in place: the usual letter in Settings, and a
 * group's own on its Letters tab. `fallback` is what an empty part says instead — the usual letter's words — shown
 * as the box's placeholder and counted as what will go.
 */
export function LetterSheet({ d, language, offer, ask, onOffer, onAsk, onOfferDone, onAskDone, mayEdit, fallback }: {
  d: SetupResponse;
  language: string;
  offer: string;
  ask: string;
  onOffer: (v: string) => void;
  onAsk: (v: string) => void;
  onOfferDone: () => void;
  onAskDone: () => void;
  mayEdit: boolean;
  fallback?: { offer: string; ask: string };
}) {
  const frame = letterFrame(language);
  const opener = EXAMPLE_OPENERS[frame.language] ?? EXAMPLE_OPENERS["en"]!;
  const sender = d.sender;
  const signOff = sender ? signature(sender) : "";
  const footer = sender
    ? letterFooter({
      language, business: sender.business, company: EXAMPLE.company, toEmail: EXAMPLE.toEmail,
      opener: "", offer: "", ask: "", signsAsName: sender.signsAsName, signsAsRole: sender.signsAsRole,
      website: sender.website, footerCompany: sender.footerCompany, footerAddress: sender.footerAddress,
    })
    : "";
  const goesOffer = offer.trim() || fallback?.offer || "";
  const goesAsk = ask.trim() || fallback?.ask || "";
  // What the letter will actually be, so the count is the one the guard will apply.
  const words = wordsIn([frame.greeting, opener, goesOffer, goesAsk, signOff].join("\n\n"));
  // The guard's own rules on the two parts written here, as they are typed: each goes into every letter unchanged,
  // so what it refuses here it refuses in every letter, and it used to say so only at Send (2026-09-29).
  const held = guardOwnWords(goesOffer, goesAsk, d.settings.neverWords);

  return (
    <>
      <div className="rounded-xl border border-line bg-page px-3.5 py-3 flex flex-col gap-2.5 text-[12.5px] leading-relaxed">
        <Fixed>{frame.greeting}</Fixed>

        <div className="flex flex-col gap-0.5">
          <Fixed italic>{opener}</Fixed>
          <Note>written for each company from what was read about it, with the letter's subject — the only two parts a model writes, and no letter goes without them</Note>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">The offer</span>
          <textarea
            aria-label="The offer"
            value={offer} onChange={(e) => onOffer(e.target.value)} rows={4} disabled={!mayEdit}
            placeholder={fallback?.offer ?? ""}
            onBlur={onOfferDone}
            className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[12.5px] leading-relaxed"
          />
          <Note>
            the small, concrete first thing you can give a new customer. Your words, never reworded
            {fallback ? " · left empty, it says your usual letter's" : ""}
          </Note>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">The ask</span>
          <textarea
            aria-label="The ask"
            value={ask} onChange={(e) => onAsk(e.target.value)} rows={2} disabled={!mayEdit}
            placeholder={fallback?.ask ?? ""}
            onBlur={onAskDone}
            className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[12.5px] leading-relaxed"
          />
          <Note>one line, small enough that refusing it would be rude</Note>
          <AskForALink ask={goesAsk} answersSeen={d.answersSeen} />
        </div>

        {held.length ? (
          <div role="alert" className="rounded-lg border border-bad/40 bg-panel px-2.5 py-2 flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-bad">No letter can go with these words: the guard would stop every one.</span>
            {held.map((r) => (
              <span key={`${r.rule}:${r.found}`} className="text-[11.5px] text-ink-2">
                {r.says}{r.found ? <> — <b>{r.found}</b></> : null}
              </span>
            ))}
          </div>
        ) : null}

        <div className="flex flex-col gap-0.5">
          {sender
            ? <Fixed>{signOff}</Fixed>
            : <Note>No mailbox connected yet, so nobody signs this. Business → Customers.</Note>}
          <Note>from your mailbox and your Business page</Note>
        </div>

        <div className="border-t border-line pt-2 flex flex-col gap-0.5">
          <span className="text-[10.5px] text-faint leading-relaxed">{footer}</span>
          <Note>the law's words, and nobody edits them</Note>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className={`text-[11.5px] ${words > LETTER_MAX_WORDS ? "text-bad" : "text-faint"}`}>
          {words} words{words > LETTER_MAX_WORDS ? ` · over ${LETTER_MAX_WORDS}, and nothing this long goes` : ""}
        </span>
        <span className="text-[11.5px] text-faint ml-auto">Plain text. No logo, no pictures — a letter a person could have typed.</span>
      </div>
    </>
  );
}

/** Words no letter of any group may carry, checked in every letter, in every language (38: a Settings card). */
export function NeverWords({ d, keep }: { d: SetupResponse; keep: (d: SetupResponse) => void }) {
  const save = useMutation({ mutationFn: (s: unknown) => api.saveCustomerSettings(s), onSuccess: keep });
  const [word, setWord] = useState("");
  return (
    <Card title="Words never to write" aside={<span className="text-xs text-muted">checked in every letter of every group</span>}>
      <div className="flex flex-wrap gap-1.5 items-center">
        {d.settings.neverWords.map((w) => (
          <span key={w} className="px-2 py-0.5 rounded-lg bg-page border border-line-2 text-xs flex items-center gap-1">
            {w}
            {d.mayEdit ? <button aria-label={`Remove ${w}`} onClick={() => save.mutate({ neverWords: d.settings.neverWords.filter((x) => x !== w) })} className="text-faint"><Icon name="x" size={10} /></button> : null}
          </span>
        ))}
        {!d.settings.neverWords.length ? <span className="text-xs text-faint">None yet.</span> : null}
        {d.mayEdit ? (
          <span className="flex items-center gap-1">
            <input value={word} onChange={(e) => setWord(e.target.value)} aria-label="Add a word" placeholder="a word" className="w-24 px-2 py-0.5 rounded-lg border border-dashed border-line-2 bg-panel text-xs" />
            <button onClick={() => { if (word.trim()) { save.mutate({ neverWords: [...d.settings.neverWords, word.trim()] }); setWord(""); } }} className="text-xs font-medium text-coral">Add</button>
          </span>
        ) : null}
      </div>
    </Card>
  );
}

/** A part of the letter nobody types here: drawn as it will be sent, and not as a disabled box pretending to be one. */
const Fixed = ({ children, italic }: { children: React.ReactNode; italic?: boolean }) => (
  <p className={`whitespace-pre-wrap ${italic ? "italic text-muted" : ""}`}>{children}</p>
);

const Note = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[10.5px] text-faint leading-relaxed">{children}</span>
);

/**
 * Said under an ask, as it is typed, when nothing here would ever see the answer it asks for (the owner, 2026-10-21).
 *
 * A send-only mailbox's answers land in its owner's inbox, so a letter asking for a reply is counted nowhere and
 * learned from by nothing. A link to book a call or to try what is offered, on the business's own website, is a click
 * Analytics counts. Advice, never a refusal: the owner still reads the replies that come.
 */
export function AskForALink({ ask, answersSeen }: { ask: string; answersSeen: boolean }) {
  if (answersSeen || !ask.trim() || asksForALink(ask)) return null;
  return (
    <span role="note" className="text-[11.5px] text-coral-2 leading-relaxed">
      Joogoos does not see the answers to your letters, so an ask for a reply is counted nowhere. Ask for a click
      instead: a link to book a call, or to try what you offer, on your own website.
    </span>
  );
}

